// The one modal sheet: a labelled dialog over a scrim, focus trapped inside, Escape, the close
// button and a swipe down dismiss it, and focus returns to what opened it. Its content scrolls at
// 88dvh while the grab handle and the close button stay pinned above it. Every popup in the app
// (menus, pickers, confirms) is a Sheet.
import { useEffect, useRef, useState } from "react";
import { View, YStack } from "tamagui";
import { useT } from "../lib/i18n";
import { IconButton } from "./controls";
import { GestureArea } from "./gestures/GestureArea";
import { Icon } from "./Icon";

export interface SheetProps {
  label: string;
  onClose: () => void;
  children: React.ReactNode;
  testId?: string;
  /** Hides the close button and the swipe for a confirm that offers its own choices. */
  closable?: boolean;
}

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
/** Pixels down, or a flick, that dismisses. */
const DISMISS_PX = 96;

export function Sheet({
  label,
  onClose,
  children,
  testId,
  closable = true,
}: SheetProps): React.JSX.Element {
  const { t } = useT();
  const root = useRef<React.ComponentRef<typeof View>>(null);
  const dialog = (): HTMLElement | null =>
    root.current instanceof HTMLElement ? root.current : null;
  const opener = useRef<Element | null>(null);
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    opener.current = document.activeElement;
    const first = dialog()?.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? dialog())?.focus();
    return () => {
      if (opener.current instanceof HTMLElement) opener.current.focus();
    };
  }, []);

  const onKeyDown = (event: React.KeyboardEvent): void => {
    if (event.key === "Escape") {
      event.stopPropagation();
      if (closable) onClose();
      return;
    }
    if (event.key !== "Tab") return;
    const items = [...(dialog()?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? [])];
    const first = items[0];
    const last = items.at(-1);
    if (first === undefined || last === undefined) return;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return (
    <View
      className="ph-no-capture"
      position="fixed"
      top={0}
      left={0}
      right={0}
      bottom={0}
      zIndex="5"
      backgroundColor="rgba(10, 4, 7, 0.55)"
      alignItems="center"
      justifyContent="flex-end"
      onKeyDown={onKeyDown}
    >
      <GestureArea
        axis="y"
        disabled={!closable}
        onDrag={({ dy, active }) => setOffset(active ? Math.max(0, dy) : 0)}
        onSwipe={{ down: onClose }}
        swipeDistance={DISMISS_PX}
        width="100%"
        maxWidth={480}
        alignItems="center"
        paddingTop="2"
      >
        <View
          ref={root}
          role="dialog"
          aria-modal={true}
          aria-label={label}
          tabIndex={-1}
          data-testid={testId}
          width="100%"
          maxHeight="88dvh"
          overflow="hidden"
          backgroundColor="surface"
          borderTopLeftRadius="4"
          borderTopRightRadius="4"
          boxShadow="var(--shSheet)"
          style={{ transform: `translateY(${offset}px)` }}
        >
          {/* Outside the scrolling region, so the close button is always in reach. */}
          <View
            flexShrink={0}
            alignItems="center"
            paddingTop="3"
            minHeight={closable ? "var(--uiTarget)" : undefined}
          >
            <View
              width={40}
              height={4}
              borderRadius="pill"
              backgroundColor="lineStrong"
              aria-hidden
            />
            {closable && (
              <IconButton
                look="ghost"
                aria-label={t("common.nav.close")}
                onPress={onClose}
                position="absolute"
                top="2"
                right="2"
              >
                <Icon name="x" size={18} />
              </IconButton>
            )}
          </View>
          <View
            data-testid="sheet-scroll"
            flexShrink={1}
            minHeight={0}
            overflow="scroll"
            paddingHorizontal="5"
            paddingBottom="6"
            paddingTop="2"
          >
            <YStack gap="3">{children}</YStack>
          </View>
        </View>
      </GestureArea>
    </View>
  );
}
